import type { FormatLocale } from '#layers/base/types/locale';
import { Temporal } from 'temporal-polyfill';

export const TIMEZONE_LOCAL = 'local';
export const TIMEZONE_UTC = 'UTC';

/**
 * Deliberately not memoised, unlike {@link getBrowserLocale}: `Intl` re-reads the host zone on every
 * call and browsers propagate an OS timezone change into a live page, so caching it would pin a
 * travelling user's tab to their departure zone until they reloaded.
 */
export function getLocalTimezone(): string {
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function resolveTimezone(timezone: string): string {
	if (timezone === TIMEZONE_LOCAL) {
		return getLocalTimezone();
	}

	return timezone;
}

export function isValidTimezone(timezone: string): boolean {
	try {
		const resolved = resolveTimezone(timezone);
		const formatter = new Intl.DateTimeFormat('en-US', { timeZone: resolved });
		return formatter.resolvedOptions().timeZone !== undefined;
	}
	catch {
		return false;
	}
}

export function toZonedDateTime(
	dateTime: Temporal.PlainDateTime,
	timezone: string,
): Temporal.ZonedDateTime {
	// Note: temporal-polyfill uses 'compatible' disambiguation by default
	// During DST gaps, ambiguous times are resolved to the later valid time
	return dateTime.toZonedDateTime(resolveTimezone(timezone));
}

export function nowInTimezone(timezone: string): Temporal.ZonedDateTime {
	return Temporal.Now.zonedDateTimeISO(resolveTimezone(timezone));
}

/**
 * Formats a timezone offset as a string (e.g., "+02:00", "-05:00", "+00:00")
 */
export function formatTimezoneOffset(timezone: string, instant?: Temporal.Instant): string {
	const resolvedTimezone = resolveTimezone(timezone);
	const atInstant = instant ?? Temporal.Now.instant();
	const zoned = atInstant.toZonedDateTimeISO(resolvedTimezone);

	return zoned.offset;
}

const timezoneFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * Constructing an `Intl.DateTimeFormat` costs ~20µs and dominates both formatters below, which run
 * per datetime cell. The key space is bounded by the locales and zones a session actually renders,
 * so the cache needs no eviction. A rejected timezone throws before it is stored.
 */
function getTimezoneFormatter(cacheKey: string, options: Intl.DateTimeFormatOptions, locale: FormatLocale): Intl.DateTimeFormat {
	let formatter = timezoneFormatters.get(cacheKey);

	if (!formatter) {
		formatter = new Intl.DateTimeFormat(locale, options);
		timezoneFormatters.set(cacheKey, formatter);
	}

	return formatter;
}

interface FormatTimezoneLabelOptions {
	locale: FormatLocale;
	/** Reference point for the label, since the abbreviation changes across a DST boundary. */
	dateTime?: Temporal.PlainDateTime;
}

/**
 * Formats a timezone as a short label (e.g., "PST", "EST", "UTC")
 * Falls back to offset format if short name is not available
 */
export function formatTimezoneLabel(
	timezone: string,
	{ locale, dateTime }: FormatTimezoneLabelOptions,
): string {
	const resolvedTimezone = resolveTimezone(timezone);

	if (resolvedTimezone === TIMEZONE_UTC) {
		return 'UTC';
	}

	const refDateTime = dateTime ?? Temporal.Now.plainDateTimeISO();
	const zonedDateTime = refDateTime.toZonedDateTime(resolvedTimezone);

	try {
		const formatter = getTimezoneFormatter(`label|${locale}|${resolvedTimezone}`, {
			timeZone: resolvedTimezone,
			timeZoneName: 'short',
		}, locale);

		const parts = formatter.formatToParts(new Date(Number(zonedDateTime.epochMilliseconds)));
		const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

		if (timeZonePart) {
			return timeZonePart.value;
		}
	}
	catch {
		console.error(`Invalid timezone: ${resolvedTimezone}`);
	}

	return zonedDateTime.offset;
}

export function generateTimezoneTooltip(
	dateTime: Temporal.PlainDateTime | null,
	currentTimezone: string,
	locale: FormatLocale,
): string | null {
	if (!dateTime) return null;

	const resolvedCurrentTz = resolveTimezone(currentTimezone);

	if (resolvedCurrentTz === TIMEZONE_UTC) {
		return null;
	}

	const zonedInCurrent = dateTime.toZonedDateTime(resolvedCurrentTz);
	const instant = zonedInCurrent.toInstant();

	// The options are fixed and the zone is always UTC, so the locale alone identifies the formatter.
	const formatter = getTimezoneFormatter(`tooltip|${locale}`, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
		timeZone: TIMEZONE_UTC,
	}, locale);

	return formatter.format(new Date(Number(instant.epochMilliseconds)));
}

export function getCommonTimezones(): Array<{ id: string; label: string; offset: string }> {
	const instant = Temporal.Now.instant();
	const commonZones = [
		'UTC',
		'America/New_York',
		'America/Chicago',
		'America/Denver',
		'America/Los_Angeles',
		'America/Anchorage',
		'Pacific/Honolulu',
		'Europe/London',
		'Europe/Paris',
		'Europe/Berlin',
		'Europe/Moscow',
		'Asia/Dubai',
		'Asia/Kolkata',
		'Asia/Singapore',
		'Asia/Tokyo',
		'Asia/Shanghai',
		'Australia/Sydney',
		'Pacific/Auckland',
	];

	return commonZones.map((id) => ({
		id,
		label: id,
		offset: formatTimezoneOffset(id, instant),
	}));
}

export function getTimezoneSelectOptions(): Array<{ label: string; value: string }> {
	return [
		{
			label: '$t:field_types.datetime.options.local',
			value: TIMEZONE_LOCAL,
		},
		...getCommonTimezones().map(({ id, label, offset }) => ({
			label: `${label} (${offset})`,
			value: id,
		})),
	];
}
